import { motion } from 'framer-motion'
import { cn } from '@/lib/utils'

interface SkeletonProps {
  className?: string
  variant?: 'default' | 'circular' | 'text' | 'card'
  animate?: boolean
}

export function Skeleton({
  className,
  variant = 'default',
  animate = true,
}: SkeletonProps) {
  const baseClasses = 'bg-white/5 overflow-hidden relative'

  const variantClasses = {
    default: 'rounded-lg',
    circular: 'rounded-full',
    text: 'rounded h-4',
    card: 'rounded-xl',
  }

  return (
    <div
      className={cn(baseClasses, variantClasses[variant], className)}
    >
      {animate && (
        <motion.div
          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent"
          initial={{ x: '-100%' }}
          animate={{ x: '100%' }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: 'linear',
          }}
        />
      )}
    </div>
  )
}

// Pre-built skeleton patterns for common use cases

export function WorkflowCardSkeleton() {
  return (
    <div className="bg-primary-mid/40 backdrop-blur-md border border-white/10 rounded-2xl p-6">
      <div className="flex items-center gap-2 mb-3">
        <Skeleton className="h-5 w-20" />
        <Skeleton className="h-5 w-16" />
      </div>
      <Skeleton className="h-6 w-3/4 mb-2" />
      <Skeleton variant="text" className="w-full mb-1" />
      <Skeleton variant="text" className="w-5/6 mb-1" />
      <Skeleton variant="text" className="w-2/3" />
      <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/10">
        <div className="flex items-center gap-4">
          <Skeleton className="h-4 w-10" />
          <Skeleton className="h-4 w-10" />
          <Skeleton className="h-4 w-10" />
        </div>
        <div className="flex items-center gap-2">
          <Skeleton variant="circular" className="w-6 h-6" />
          <Skeleton className="h-4 w-16" />
        </div>
      </div>
    </div>
  )
}

export function WorkflowDetailSkeleton() {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-primary-mid/40 backdrop-blur-md border border-white/10 rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <Skeleton className="h-6 w-24" />
          <Skeleton className="h-6 w-20" />
        </div>
        <Skeleton className="h-10 w-2/3 mb-4" />
        <Skeleton variant="text" className="w-full mb-1" />
        <Skeleton variant="text" className="w-4/5" />
        <div className="flex items-center gap-4 mt-6">
          <Skeleton variant="circular" className="w-10 h-10" />
          <div>
            <Skeleton className="h-5 w-32 mb-1" />
            <Skeleton className="h-4 w-24" />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="bg-primary-mid/40 backdrop-blur-md border border-white/10 rounded-2xl p-6">
        <Skeleton variant="text" className="w-full mb-2" />
        <Skeleton variant="text" className="w-full mb-2" />
        <Skeleton variant="text" className="w-3/4 mb-4" />
        <Skeleton className="h-48 w-full mb-4" />
        <Skeleton variant="text" className="w-full mb-2" />
        <Skeleton variant="text" className="w-5/6 mb-2" />
        <Skeleton variant="text" className="w-2/3" />
      </div>
    </div>
  )
}

export function CommentSkeleton() {
  return (
    <div className="flex gap-4 p-4 bg-primary-mid/40 backdrop-blur-md border border-white/10 rounded-xl">
      <Skeleton variant="circular" className="w-10 h-10 flex-shrink-0" />
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-3 w-16" />
        </div>
        <Skeleton variant="text" className="w-full mb-1" />
        <Skeleton variant="text" className="w-4/5 mb-1" />
        <Skeleton variant="text" className="w-2/3" />
      </div>
    </div>
  )
}

export function UserProfileSkeleton() {
  return (
    <div className="space-y-8">
      {/* Profile Header */}
      <div className="bg-primary-mid/40 backdrop-blur-md border border-white/10 rounded-2xl p-6">
        <div className="flex flex-col md:flex-row gap-6 items-center md:items-start">
          <Skeleton variant="circular" className="w-24 h-24 flex-shrink-0" />
          <div className="flex-1 w-full">
            <Skeleton className="h-8 w-48 mb-2 mx-auto md:mx-0" />
            <Skeleton className="h-5 w-32 mb-3 mx-auto md:mx-0" />
            <Skeleton variant="text" className="w-full max-w-xl mb-1 mx-auto md:mx-0" />
            <Skeleton variant="text" className="w-4/5 max-w-xl mb-4 mx-auto md:mx-0" />
            <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 mb-4">
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-4 w-20" />
            </div>
            <div className="flex flex-wrap items-center justify-center md:justify-start gap-2">
              <Skeleton className="h-9 w-20" />
              <Skeleton className="h-9 w-24" />
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="bg-primary-mid/40 backdrop-blur-md border border-white/10 rounded-2xl p-4 text-center">
            <Skeleton variant="circular" className="w-8 h-8 mx-auto mb-2" />
            <Skeleton className="h-7 w-16 mx-auto mb-1" />
            <Skeleton className="h-4 w-20 mx-auto" />
          </div>
        ))}
      </div>
    </div>
  )
}

export function ActivityItemSkeleton() {
  return (
    <div className="flex items-center gap-3 p-4 bg-primary-mid/20 border border-white/5 rounded-xl">
      <Skeleton variant="circular" className="w-10 h-10 flex-shrink-0" />
      <div className="flex-1">
        <Skeleton variant="text" className="w-3/4 mb-1" />
        <Skeleton className="h-3 w-20" />
      </div>
    </div>
  )
}

export function SearchResultsSkeleton({ count = 6 }: { count?: number }) {
  return (
    <div className="grid md:grid-cols-2 gap-4">
      {Array.from({ length: count }).map((_, i) => (
        <WorkflowCardSkeleton key={i} />
      ))}
    </div>
  )
}

export function SidebarSkeleton() {
  return (
    <div className="space-y-4">
      {/* Categories */}
      <div className="bg-primary-mid/40 backdrop-blur-md border border-white/10 rounded-2xl overflow-hidden">
        <div className="p-4 border-b border-white/10">
          <Skeleton className="h-5 w-24" />
        </div>
        <div className="p-2 space-y-1">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="flex items-center justify-between px-3 py-2">
              <Skeleton className="h-4 w-28" />
              <Skeleton className="h-5 w-8 rounded-full" />
            </div>
          ))}
        </div>
      </div>

      {/* Tags */}
      <div className="bg-primary-mid/40 backdrop-blur-md border border-white/10 rounded-2xl overflow-hidden">
        <div className="p-4 border-b border-white/10">
          <Skeleton className="h-5 w-16" />
        </div>
        <div className="p-4 space-y-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="flex items-center gap-2">
              <Skeleton className="h-4 w-4" />
              <Skeleton className="h-4 w-20" />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export function ContributionGraphSkeleton() {
  return (
    <div className="bg-primary-mid/40 backdrop-blur-md border border-white/10 rounded-2xl p-6">
      <div className="flex items-center justify-between mb-4">
        <Skeleton className="h-6 w-40" />
        <Skeleton className="h-4 w-32" />
      </div>

      {/* Calendar grid */}
      <div className="overflow-x-auto">
        <div className="grid grid-rows-7 grid-flow-col gap-1" style={{ minWidth: '720px' }}>
          {Array.from({ length: 53 * 7 }).map((_, i) => (
            <Skeleton key={i} className="w-3 h-3 rounded-sm" animate={false} />
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center justify-end gap-1 mt-4">
        <span className="text-xs text-neutral-500 mr-2">Less</span>
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="w-3 h-3 rounded-sm" animate={false} />
        ))}
        <span className="text-xs text-neutral-500 ml-2">More</span>
      </div>
    </div>
  )
}

export function VersionTimelineSkeleton() {
  return (
    <div className="space-y-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="flex gap-4">
          <div className="flex flex-col items-center">
            <Skeleton variant="circular" className="w-10 h-10" />
            {i < 3 && <Skeleton className="w-0.5 h-16 mt-2" />}
          </div>
          <div className="flex-1 bg-primary-mid/40 backdrop-blur-md border border-white/10 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Skeleton className="h-5 w-16" />
              <Skeleton className="h-4 w-24" />
            </div>
            <Skeleton variant="text" className="w-3/4 mb-1" />
            <Skeleton variant="text" className="w-1/2" />
          </div>
        </div>
      ))}
    </div>
  )
}
